export type Viewport = {
  start: number;
  end: number;
};

export type Extent = {
  min: number;
  max: number;
};

function getMinSpan(extent: Extent) {
  const span = extent.max - extent.min;
  if (!Number.isFinite(span) || span <= 0) return 1;
  return Math.max(span / 1000, 1);
}

export function clampViewport(domain: Viewport, extent: Extent) {
  const minSpan = getMinSpan(extent);
  let start = domain.start;
  let end = domain.end;

  if (!Number.isFinite(start) || !Number.isFinite(end) || start === end) {
    return { start: extent.min, end: extent.max };
  }

  if (start > end) {
    [start, end] = [end, start];
  }

  if (end - start < minSpan) {
    const mid = (start + end) / 2;
    start = mid - minSpan / 2;
    end = mid + minSpan / 2;
  }

  const span = end - start;
  if (span >= extent.max - extent.min) {
    return { start: extent.min, end: extent.max };
  }

  if (start < extent.min) {
    start = extent.min;
    end = start + span;
  }
  if (end > extent.max) {
    end = extent.max;
    start = end - span;
  }

  return { start, end };
}

export function zoomViewport(domain: Viewport, extent: Extent, target: number, scale: number) {
  const minSpan = getMinSpan(extent);
  const currentSpan = domain.end - domain.start;
  if (!Number.isFinite(currentSpan) || currentSpan <= 0) return clampViewport(domain, extent);

  const clampedScale = Math.max(scale, minSpan / (extent.max - extent.min));
  const nextSpan = Math.min(Math.max(currentSpan * clampedScale, minSpan), extent.max - extent.min);
  const ratio = currentSpan === 0 ? 0.5 : (target - domain.start) / currentSpan;
  const start = target - ratio * nextSpan;
  const end = start + nextSpan;

  return clampViewport({ start, end }, extent);
}

export function panViewport(domain: Viewport, extent: Extent, delta: number) {
  const span = domain.end - domain.start;
  if (!Number.isFinite(span) || span <= 0) {
    return clampViewport(domain, extent);
  }

  let start = domain.start + delta;
  let end = domain.end + delta;

  if (start < extent.min) {
    start = extent.min;
    end = start + span;
  } else if (end > extent.max) {
    end = extent.max;
    start = end - span;
  }

  return clampViewport({ start, end }, extent);
}
